import type {
  AccountAccessToken,
  FormattedPriceSettings,
  PriceSettingsType,
  Session,
} from '@setplex/tria-api'
import { getPriceSettingsExternalPaymentData } from '~/entities/price-settings'
import {
  MediaTypesExtended,
  type MediaTypesEx,
} from '~/features/payment/generic'
import { type ISelectedPaymentContent } from '~/features/payment/generic/model'
import {
  getPaymentSeasonMediaType,
  getPaymentTvShowMediaType,
} from '~/features/payment/lib'
import { getPortalAuthenticatedUrl } from '~/features/portal/redirect'
import type { Entity } from '../index.h'

const isMediaTypeBundle = (mediaType?: MediaTypesEx | null): boolean => {
  return (
    mediaType === MediaTypesExtended.VOD_BUNDLE ||
    mediaType === MediaTypesExtended.VOD_IN_BUNDLE ||
    mediaType === MediaTypesExtended.TV_SHOW_BUNDLE ||
    mediaType === MediaTypesExtended.TV_SHOW_IN_BUNDLE ||
    mediaType === MediaTypesExtended.TV_CHANNEL_BUNDLE ||
    mediaType === MediaTypesExtended.TV_CHANNEL_IN_BUNDLE ||
    mediaType === MediaTypesExtended.TV_SHOW_SEASON_IN_BUNDLE ||
    mediaType === MediaTypesExtended.TV_SHOW_EPISODE_IN_BUNDLE
  )
}

interface GetExternalPaymentUrlHelperParams {
  baseUrl: string
  session: Session | null
  selectedContent: ISelectedPaymentContent
  token: AccountAccessToken | null
}
export function getExternalPaymentUrlHelper({
  baseUrl,
  session,
  selectedContent,
  token,
}: GetExternalPaymentUrlHelperParams): string | null {
  const url = getPortalAuthenticatedUrl({ baseUrl, token, ...session })
  if (!url) return null

  const { externalId, priceType, mediaType } = selectedContent || {}

  if (externalId) {
    if (mediaType === MediaTypesExtended.PARTNER_PRODUCT) {
      url.searchParams.set('productId', externalId)
    } else if (isMediaTypeBundle(mediaType)) {
      url.searchParams.set('addOnId', externalId)
    } else {
      url.searchParams.set('itemId', externalId)
    }
  }

  if (priceType) {
    url.searchParams.set('priceType', priceType)
  }

  url.searchParams.set('client', 'wbs')
  return url.toString()
}

export const getPaymentTvShowSeasonPriceSettings = (
  season?: Entity
): FormattedPriceSettings[] | undefined => {
  if (!season) return

  const { seasonPriceSettings, tvShowPriceSettings } = season

  if (seasonPriceSettings && seasonPriceSettings.length) {
    return seasonPriceSettings
  }

  if (tvShowPriceSettings && tvShowPriceSettings.length) {
    return tvShowPriceSettings
  }
}

export const getPaymentTvShowSeasonMediaType = (
  season: Entity | undefined
): MediaTypesEx | undefined => {
  if (!season) return
  const { seasonPriceSettings, tvShowPriceSettings } = season

  if (seasonPriceSettings && seasonPriceSettings.length) {
    return getPaymentSeasonMediaType(seasonPriceSettings)
  }

  if (tvShowPriceSettings && tvShowPriceSettings.length) {
    return getPaymentTvShowMediaType(tvShowPriceSettings)
  }
}

interface ExternalPaymentDataByMediaType {
  externalId?: string | null
  priceType?: PriceSettingsType
  mediaType?: MediaTypesEx
}
interface GetExternalPaymentDataByMediaTypeParams {
  mediaType: MediaTypesEx | null | undefined
  entity: Entity | undefined
  externalId?: string
}
export const getExternalPaymentDataByMediaType = ({
  mediaType,
  entity,
  externalId,
}: GetExternalPaymentDataByMediaTypeParams): ExternalPaymentDataByMediaType => {
  const paymentExternalId = externalId ?? entity?.externalId

  const config: Record<string, ExternalPaymentDataByMediaType> = {
    [MediaTypesExtended.PARTNER_PRODUCT]: {
      externalId: paymentExternalId,
      priceType: entity?.priceType,
    },
    [MediaTypesExtended.TV_SHOW_SEASON]: {
      ...getPriceSettingsExternalPaymentData(
        getPaymentTvShowSeasonPriceSettings(entity),
        paymentExternalId
      ),
      mediaType: getPaymentTvShowSeasonMediaType(entity),
    },
  }
  const defaultConfig = getPriceSettingsExternalPaymentData(
    entity?.priceSettings,
    paymentExternalId
  )

  if (!mediaType) return defaultConfig

  return config[mediaType] ?? defaultConfig
}
